import type { Display_DisplayGroup } from "@smartsuite/automation-engine-api";
import { observer } from "mobx-react-lite";
import * as R from "remeda";
import * as A from "../../../../../stores/automation/mergers/actions";
import { useRootStore } from "../../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
} from "../../../../../types/candidate";
import { FieldEditor } from "../../../Forms/InputForm/FieldEditor";
import { shouldShowActionInputValidationErrors } from "../../../../../utils/action-helpers";

export const GroupFieldList = observer<{
  loading: boolean;
  action: ActionCandidate;
  onInputChange: (input: InputCandidate, action: ActionCandidate) => void;
  onRemoveFieldInput: (input: InputCandidate, action: ActionCandidate) => void;
  afterConditions: boolean;
}>(
  ({ loading, action, onInputChange, onRemoveFieldInput, afterConditions }) => {
    const store = useRootStore();
    const { candidate } = store.automations;
    const groupFields = findGroupFields(action, afterConditions);
    const actionGroupType =
      store.automations.candidate.currentActionGroup?.config.oneofKind ??
      "actions";

    const needToShowError = (hash: string): boolean => {
      if (hash !== action.hash) {
        return candidate.isActionSubmitted(hash);
      }

      return shouldShowActionInputValidationErrors(
        candidate,
        action,
        store.automations.isAllDynamicDescriptionsLoaded(),
      );
    };

    return (
      <>
        {filterAiFIelds(groupFields).map((groupField) => {
          return (
            <FieldEditor
              key={`${action.hash}-${groupField.groupId}`}
              groupLabel={groupField.label}
              groupSubLabel={groupField.subLabel}
              tooltip={groupField.tooltip}
              collapsible={groupField.collapsible}
              showValidationError={needToShowError(action.hash)}
              loading={loading}
              fieldInputs={groupField.inputs}
              action={action}
              onFieldInputChange={(input, action) => {
                if (
                  groupField.inputs.find((i) => i.inputId === input.inputId)
                    ?.isOpen
                ) {
                  onInputChange(input, action);
                  return;
                }

                const lastOpenInputIdInGroup = groupField.inputs
                  .reverse()
                  .find((input) => input.isOpen)?.inputId;

                if (
                  groupField.foldedByDefault &&
                  R.isTruthy(lastOpenInputIdInGroup)
                ) {
                  candidate.setActions(
                    [
                      A.mergeInputList(
                        action.hash,
                        getNextInputList(
                          action.inputs,
                          input,
                          lastOpenInputIdInGroup ?? "",
                        ),
                      ),
                    ],
                    actionGroupType,
                  );
                } else {
                  onInputChange(input, action);
                }
              }}
              onRemoveFieldInput={(input) => onRemoveFieldInput(input, action)}
              foldedByDefault={groupField.foldedByDefault}
              addButtonLabel={groupField.addButtonLabel}
            />
          );
        })}
      </>
    );
  },
);
GroupFieldList.displayName = "GroupFieldList";

interface GroupFields extends Display_DisplayGroup {
  inputs: InputCandidate[];
}

// TODO refactor this fn
const findGroupFields = (
  action: ActionCandidate,
  afterConditions: boolean,
): GroupFields[] => {
  const groupDescriptions: Display_DisplayGroup[] = action.description.inputs
    .filter(
      (item) =>
        item.display?.display.oneofKind === "group" &&
        ((afterConditions && item.display?.displayAfterConditions === true) ||
          (!afterConditions && item.display?.displayAfterConditions !== true)),
    )
    .map(
      (i) =>
        i.display?.display.oneofKind === "group" && i.display.display.group,
    )
    .filter((i): i is Display_DisplayGroup => Boolean(i));

  const uniqGroups = [
    ...new Map(groupDescriptions.map((item) => [item.groupId, item])).values(),
  ];

  const inputsWithoutDesc: string[] = [];

  return uniqGroups.map((groupDescription): GroupFields => {
    const inputs = action.inputs.filter((input) => {
      const findDescription = action.description.inputs.find(
        (description) => description.inputId === input.inputId,
      );

      const res =
        (findDescription?.display?.display.oneofKind === "group" &&
          findDescription?.display?.display.group.groupId ===
            groupDescription.groupId) ||
        (findDescription === undefined &&
          !inputsWithoutDesc.includes(input.inputId));

      if (findDescription === undefined) {
        inputsWithoutDesc.push(input.inputId);
      }

      return res;
    });

    inputs.sort((a, b) => {
      const description = action.description.inputs.find(
        (description) => description.inputId === a.inputId,
      );
      if (
        description?.display?.display.oneofKind === "group" &&
        description?.display?.display.group.collapsible
      ) {
        return (
          action.description.inputs.findIndex(
            (description) => description.inputId === a.inputId,
          ) -
          action.description.inputs.findIndex(
            (description) => description.inputId === b.inputId,
          )
        );
      }
      return 0;
    });

    return {
      groupId: groupDescription.groupId,
      label: groupDescription.label,
      subLabel: groupDescription.subLabel,
      foldedByDefault: groupDescription.foldedByDefault,
      collapsible: groupDescription.collapsible,
      inputs,
      tooltip: groupDescription.tooltip,
      addButtonLabel: groupDescription.addButtonLabel,
    };
  });
};

const getNextInputList = (
  inputList: InputCandidate[],
  nextInput: InputCandidate,
  lastOpenInputIdInGroup: string,
): InputCandidate[] =>
  R.pipe(
    inputList,
    R.filter((input) => input.inputId !== nextInput.inputId),
    R.flatMap((input) =>
      input.inputId === lastOpenInputIdInGroup ? [input, nextInput] : [input],
    ),
  );

// we have to filter "output_content" on UI to improve UX for user.
const filterAiFIelds = (groupFields: GroupFields[]): GroupFields[] => {
  return groupFields.map((groupField) => {
    if (groupField.groupId === "output") {
      const inputType = groupField.inputs.find(
        (input) => input.inputId === "output_type",
      );
      if (
        inputType?.value !== undefined &&
        inputType.value.values.length > 0 &&
        inputType.value.values[0].value.oneofKind === "string" &&
        inputType.value.values[0].value.string === "simple"
      ) {
        groupField.inputs = groupField.inputs.filter(
          (input) => input.inputId !== "output_content",
        );
      }
    }
    return groupField;
  });
};
